/**
 * The caret beside the header checkbox — list engine v3 §2.5.
 *
 * The header box ticks this page. The three things people actually want from
 * it are "this page", "everything that matches" and "none", and the second of
 * those cannot be a checkbox: it is a *mode*, not fifty ticks, and the whole
 * reason selection can reach past the page at all.
 *
 * The note underneath is not decoration. A selection that survives paging and
 * dies on a filter change is surprising exactly once, and the line is there so
 * it is surprising zero times.
 */
import { IconCheck } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import type { Selection } from '../lib/selection.js'

export interface SelectionMenuProps {
  selection: Selection
  /** How many rows are on the page — what "Select this page" takes. */
  pageSize: number
  /** What the list is showing, which is what "all that match" means. */
  visibleCount: number
  /** Whether every row on this page is already in. */
  pageChecked: boolean
  onSelectPage: () => void
  onSelectAll: () => void
  onClear: () => void
}

export function SelectionMenu({
  selection, pageSize, visibleCount, pageChecked, onSelectPage, onSelectAll, onClear,
}: SelectionMenuProps) {
  const rows: { key: string; label: string; count?: number; on: boolean; run: () => void }[] = [
    {
      key: 'page',
      label: 'Select this page',
      count: pageSize,
      on: selection.mode === 'ids' && pageChecked,
      run: onSelectPage,
    },
    {
      key: 'all',
      label: `Select all ${formatNumber(visibleCount)} that match`,
      count: visibleCount,
      on: selection.mode === 'matching',
      run: onSelectAll,
    },
    { key: 'none', label: 'Select none', on: false, run: onClear },
  ]

  return (
    <div className="flex flex-col">
      <ul className="flex list-none flex-col p-1.5">
        {rows.map((row) => (
          <li key={row.key}>
            <button
              type="button"
              onClick={row.run}
              className={cn('flex h-[34px] w-full items-center gap-2 rounded-field px-2 text-left',
                row.on ? 'font-semibold text-accent' : 'text-text hover:bg-surface-alt')}
            >
              <span aria-hidden className="w-3.5 shrink-0 text-accent">
                {row.on && <IconCheck size={13} />}
              </span>
              <span className="min-w-0 flex-1 truncate text-cell">{row.label}</span>
              {row.count !== undefined && (
                <span className="tnum shrink-0 text-sub text-text-faint">
                  {formatNumber(row.count)}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
      <p className="m-0 border-t border-line-soft px-3 py-2 text-sub text-text-muted">
        Ticks stay when you change page, sort, or row height. Changing the search
        or the filter starts the selection over — the rows it described may not
        be on screen any more.
      </p>
    </div>
  )
}
